import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ChevronRight,
  AccountTree,
  Code,
  PlayArrow,
  CheckCircle,
  Error,
  Info,
  BugReport,
  ContentCopy,
  FilterList,
  ViewModule,
  ViewList,
  Schedule,
  LocalHospital,
  Payment,
  Assignment,
  Search,
  Folder,
  Settings,
  Security,
  Speed,
  Update
} from '@mui/icons-material';

interface EndpointParameter {
  name: string;
  type: string;
  required?: boolean;
  default?: string;
  description: string;
}

interface Endpoint {
  id: string;
  name: string;
  method: string;
  path: string;
  description: string;
  version: string;
  priority?: string;
  parameters: EndpointParameter[];
  categoryKey: string;
  categoryName: string;
  categoryDescription: string;
  categoryIcon: string;
}

interface CategoryData {
  category: string;
  description: string;
  icon: string;
  endpoints: Endpoint[];
}

interface TestResult {
  success: boolean;
  data?: any;
  error?: string;
  httpStatus?: number;
  timestamp: string;
  duration?: number;
}

const getCategoryIcon = (categoryKey: string) => {
  const icons: Record<string, React.ReactNode> = {
    'booking-management': <Schedule color="primary" />,
    'offer-management': <Assignment color="secondary" />,
    'availability-management': <Search color="info" />,
    'admission-management': <LocalHospital color="success" />,
    'payment-management': <Payment color="warning" />,
    'booking-process': <Update color="primary" />,
    'reference-data': <Folder color="action" />,
    'prescriptions': <Assignment color="primary" />
  };
  return icons[categoryKey] || <Settings />;
};

const getVersionColor = (version: string) => {
  switch (version) {
    case 'v4': return 'success';
    case 'v3': return 'primary';
    case 'v2': return 'warning';
    case 'v1': return 'error';
    default: return 'default';
  }
};

const getPriorityColor = (priority?: string) => {
  switch (priority) {
    case 'high': return 'error';
    case 'medium': return 'warning';
    case 'low': return 'info';
    default: return 'default';
  }
};

const TestEndpointsPage: React.FC = () => {
  const [structuredData, setStructuredData] = useState<Record<string, CategoryData>>({});
  const [flatEndpoints, setFlatEndpoints] = useState<Endpoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState<any>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const [testingEndpoint, setTestingEndpoint] = useState<string | null>(null);
  const [parameterValues, setParameterValues] = useState<Record<string, Record<string, string>>>({});
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [selectedEndpoint, setSelectedEndpoint] = useState<string | null>(null);
  const [listViewMode, setListViewMode] = useState<'tree' | 'list'>('tree');
  const [filterVersion, setFilterVersion] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [onlyV4Preferred, setOnlyV4Preferred] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [responseViewMode, setResponseViewMode] = useState<'json' | 'tree'>('json');

  useEffect(() => {
    loadEndpoints();
    checkAuthStatus();
  }, []);

  const loadEndpoints = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/recup-test/endpoints');
      const data = await response.json();
      
      if (data.success) {
        setFlatEndpoints(data.data);
        setStructuredData(data.structured || {});
        
        // Initialize parameter values
        const initialValues: Record<string, Record<string, string>> = {};
        data.data.forEach((endpoint: Endpoint) => {
          initialValues[endpoint.id] = {};
          endpoint.parameters.forEach((param: EndpointParameter) => {
            initialValues[endpoint.id][param.name] = param.default || '';
          });
        });
        setParameterValues(initialValues);
        
        // Initialize all categories as collapsed by default
        const initialExpanded: Record<string, boolean> = {};
        Object.keys(data.structured || {}).forEach(categoryKey => {
          initialExpanded[categoryKey] = false;
        });
        setExpandedCategories(initialExpanded);
      } else {
        console.error('Failed to load endpoints:', data.error);
      }
    } catch (error) {
      console.error('Error loading endpoints:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/recup-test/auth-status');
      const data = await response.json();
      setAuthStatus(data);
    } catch (error) {
      console.error('Error checking auth status:', error);
      setAuthStatus({ success: false, error: error.message });
    }
  };

  const testEndpoint = async (endpoint: Endpoint) => {
    setTestingEndpoint(endpoint.id);
    
    try {
      const parameters = parameterValues[endpoint.id] || {};
      
      // Filter out empty parameters
      const filteredParams = Object.entries(parameters)
        .filter(([key, value]) => value !== '')
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
      
      const startTime = Date.now();
      
      // Configurazione fetch con timeout per chiamate RECUP lente
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 secondi timeout
      
      const response = await fetch('/api/recup-test/test-endpoint', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpointPath: endpoint.path,
          parameters: filteredParams,
          method: endpoint.method,
          endpointId: endpoint.id
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      const result = await response.json();
      const duration = Date.now() - startTime;
      
      setTestResults(prev => ({
        ...prev,
        [endpoint.id]: {
          ...result,
          timestamp: new Date().toISOString(),
          httpStatus: response.status,
          duration
        }
      }));

    } catch (error) {
      let errorMessage = error.message;
      if (error.name === 'AbortError') {
        errorMessage = 'Request timeout dopo 60 secondi - L\'endpoint RECUP potrebbe essere lento o non disponibile';
      }
      
      setTestResults(prev => ({
        ...prev,
        [endpoint.id]: {
          success: false,
          error: errorMessage,
          timestamp: new Date().toISOString(),
          httpStatus: error.name === 'AbortError' ? 408 : undefined
        }
      }));
    } finally {
      setTestingEndpoint(null);
    }
  };

  const handleParameterChange = (endpointId: string, paramName: string, value: string) => {
    setParameterValues(prev => ({
      ...prev,
      [endpointId]: {
        ...prev[endpointId],
        [paramName]: value
      }
    }));
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getFilteredEndpoints = () => {
    let filtered = flatEndpoints;
    
    if (onlyV4Preferred) {
      filtered = filtered.filter(ep => ep.version === 'v4' || ep.priority === 'high');
    }
    
    if (filterVersion !== 'all') {
      filtered = filtered.filter(ep => ep.version === filterVersion);
    }
    
    if (filterPriority !== 'all') {
      filtered = filtered.filter(ep => ep.priority === filterPriority);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ep => 
        ep.name.toLowerCase().includes(query) ||
        ep.description.toLowerCase().includes(query) ||
        ep.path.toLowerCase().includes(query) ||
        ep.categoryName.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const getStatusIcon = (result: TestResult) => {
    if (result.success) {
      return <CheckCircle color="success" />;
    } else {
      return <Error color="error" />;
    }
  };

  const renderTreeView = () => (
    <Box>
      {Object.entries(structuredData).map(([categoryKey, categoryData]) => {
        const categoryEndpoints = categoryData.endpoints.filter(ep => 
          getFilteredEndpoints().some(filtered => filtered.id === ep.id)
        );
        
        if (categoryEndpoints.length === 0) return null;
        
        const isExpanded = expandedCategories[categoryKey];
        const testCount = categoryEndpoints.filter(ep => testResults[ep.id]).length;
        const successCount = categoryEndpoints.filter(ep => testResults[ep.id]?.success).length;
        
        return (
          <Card key={categoryKey} sx={{ mb: 2 }}>
            <CardContent sx={{ pb: 1 }}>
              <ListItemButton onClick={() => toggleCategory(categoryKey)} sx={{ px: 0 }}>
                <ListItemIcon>
                  {getCategoryIcon(categoryKey)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6">
                        {categoryData.icon} {categoryData.category}
                      </Typography>
                      <Badge badgeContent={categoryEndpoints.length} color="primary" sx={{ ml: 2 }} />
                      {testCount > 0 && (
                        <Chip 
                          size="small" 
                          label={`${successCount}/${testCount}`}
                          color={successCount === testCount ? 'success' : 'warning'}
                        />
                      )}
                    </Box>
                  }
                  secondary={categoryData.description}
                />
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              
              <Collapse in={isExpanded}>
                <List sx={{ pl: 2 }}>
                  {categoryEndpoints.map((endpoint) => (
                    <ListItem key={endpoint.id} sx={{ py: 1 }}>
                      <Paper 
                        sx={{ 
                          width: '100%', 
                          p: 2,
                          cursor: 'pointer',
                          border: selectedEndpoint === endpoint.id ? 2 : 1,
                          borderColor: selectedEndpoint === endpoint.id ? 'primary.main' : 'divider'
                        }}
                        onClick={() => setSelectedEndpoint(endpoint.id)}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {endpoint.name}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={endpoint.method} 
                              color="primary" 
                              variant="outlined"
                            />
                            <Chip 
                              size="small" 
                              label={endpoint.version} 
                              color={getVersionColor(endpoint.version)}
                            />
                            {endpoint.priority && (
                              <Chip 
                                size="small" 
                                label={endpoint.priority.toUpperCase()} 
                                color={getPriorityColor(endpoint.priority)}
                                icon={<Speed />}
                              />
                            )}
                          </Box>
                          {testResults[endpoint.id] && getStatusIcon(testResults[endpoint.id])}
                        </Box>
                        
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                          {endpoint.path}
                        </Typography>
                        
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {endpoint.description}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" gap={1}>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={testingEndpoint === endpoint.id ? <CircularProgress size={16} /> : <PlayArrow />}
                            onClick={(e) => {
                              e.stopPropagation();
                              testEndpoint(endpoint);
                            }}
                            disabled={testingEndpoint === endpoint.id}
                          >
                            {testingEndpoint === endpoint.id ? 'Testing...' : 'Test'}
                          </Button>
                          
                          <Chip 
                            size="small" 
                            label={`${endpoint.parameters.length} params`} 
                            variant="outlined"
                          />
                          
                          {testResults[endpoint.id] && (
                            <Chip 
                              size="small" 
                              label={`${testResults[endpoint.id].httpStatus || 'N/A'}`}
                              color={testResults[endpoint.id].success ? 'success' : 'error'}
                            />
                          )}
                        </Box>
                      </Paper>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );

  const renderListView = () => (
    <Grid container spacing={2}>
      {getFilteredEndpoints().map((endpoint) => (
        <Grid item xs={12} key={endpoint.id}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: selectedEndpoint === endpoint.id ? 2 : 1,
              borderColor: selectedEndpoint === endpoint.id ? 'primary.main' : 'divider'
            }}
            onClick={() => setSelectedEndpoint(endpoint.id)}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  {getCategoryIcon(endpoint.categoryKey)}
                  <Typography variant="h6">{endpoint.name}</Typography>
                  <Chip size="small" label={endpoint.method} color="primary" variant="outlined" />
                  <Chip size="small" label={endpoint.version} color={getVersionColor(endpoint.version)} />
                  {endpoint.priority && (
                    <Chip 
                      size="small" 
                      label={endpoint.priority.toUpperCase()} 
                      color={getPriorityColor(endpoint.priority)}
                      icon={<Speed />}
                    />
                  )}
                </Box>
                {testResults[endpoint.id] && getStatusIcon(testResults[endpoint.id])}
              </Box>
              
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                {endpoint.categoryName} • {endpoint.path}
              </Typography>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                {endpoint.description}
              </Typography>
              
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={testingEndpoint === endpoint.id ? <CircularProgress size={16} /> : <PlayArrow />}
                  onClick={(e) => {
                    e.stopPropagation();
                    testEndpoint(endpoint);
                  }}
                  disabled={testingEndpoint === endpoint.id}
                >
                  {testingEndpoint === endpoint.id ? 'Testing...' : 'Test'}
                </Button>
                
                <Chip 
                  size="small" 
                  label={`${endpoint.parameters.length} parameters`} 
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // Componente separato per evitare violazione delle regole dei hooks
  const JsonTreeItem: React.FC<{ data: any; nodeKey: string; level: number }> = ({ data, nodeKey, level }) => {
    const [isExpanded, setIsExpanded] = useState(level < 2);

    if (data === null || data === undefined) {
      return (
        <ListItem sx={{ pl: level * 2 }}>
          <ListItemText 
            primary={
              <Typography variant="body2" color="textSecondary">
                null
              </Typography>
            }
          />
        </ListItem>
      );
    }

    if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') {
      return (
        <ListItem sx={{ pl: level * 2 }}>
          <ListItemText 
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="primary" fontWeight="bold">
                  {typeof data === 'string' ? `"${data}"` : String(data)}
                </Typography>
                <Chip 
                  label={typeof data} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '10px', height: '16px' }}
                />
              </Box>
            }
          />
        </ListItem>
      );
    }

    if (Array.isArray(data)) {
      return (
        <Box>
          <ListItem sx={{ pl: level * 2 }}>
            <ListItemButton onClick={() => setIsExpanded(!isExpanded)} sx={{ p: 0 }}>
              <ListItemIcon>
                {isExpanded ? <ExpandMore /> : <ChevronRight />}
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Array</Typography>
                    <Chip 
                      label={`${data.length} items`} 
                      size="small" 
                      color="secondary"
                      sx={{ fontSize: '10px', height: '16px' }}
                    />
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
          {isExpanded && data.map((item, index) => (
            <Box key={`${nodeKey}-${index}`}>
              <ListItem sx={{ pl: (level + 1) * 2 }}>
                <Typography variant="body2" color="textSecondary" sx={{ minWidth: '50px' }}>
                  [{index}]
                </Typography>
              </ListItem>
              <JsonTreeItem data={item} nodeKey={`${nodeKey}-${index}`} level={level + 2} />
            </Box>
          ))}
        </Box>
      );
    }

    if (typeof data === 'object') {
      const keys = Object.keys(data);
      return (
        <Box>
          <ListItem sx={{ pl: level * 2 }}>
            <ListItemButton onClick={() => setIsExpanded(!isExpanded)} sx={{ p: 0 }}>
              <ListItemIcon>
                {isExpanded ? <ExpandMore /> : <ChevronRight />}
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight="bold">Object</Typography>
                    <Chip 
                      label={`${keys.length} keys`} 
                      size="small" 
                      color="info"
                      sx={{ fontSize: '10px', height: '16px' }}
                    />
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
          {isExpanded && keys.map(objKey => (
            <Box key={`${nodeKey}-${objKey}`}>
              <ListItem sx={{ pl: (level + 1) * 2 }}>
                <Typography variant="body2" color="textPrimary" fontWeight="medium" sx={{ minWidth: '100px' }}>
                  {objKey}:
                </Typography>
              </ListItem>
              <JsonTreeItem data={data[objKey]} nodeKey={`${nodeKey}-${objKey}`} level={level + 2} />
            </Box>
          ))}
        </Box>
      );
    }

    return null;
  };

  const renderEndpointDetails = () => {
    if (!selectedEndpoint) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center', height: '100%' }}>
          <Info sx={{ fontSize: 48, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            Seleziona un endpoint per vedere i dettagli
          </Typography>
        </Paper>
      );
    }

    const endpoint = flatEndpoints.find(ep => ep.id === selectedEndpoint);
    if (!endpoint) return null;

    const result = testResults[endpoint.id];

    return (
      <Paper sx={{ p: 3, height: '100%', overflow: 'auto' }}>
        <Box mb={3}>
          <Typography variant="h5" gutterBottom>
            {endpoint.name}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Chip label={endpoint.method} color="primary" />
            <Chip label={endpoint.version} color={getVersionColor(endpoint.version)} />
            {endpoint.priority && (
              <Chip 
                label={endpoint.priority.toUpperCase()} 
                color={getPriorityColor(endpoint.priority)}
                icon={<Speed />}
              />
            )}
          </Box>
          <Typography variant="body1" paragraph>
            {endpoint.description}
          </Typography>
          <Typography variant="body2" color="textSecondary" fontFamily="monospace">
            {endpoint.path}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Parametri ({endpoint.parameters.length})
        </Typography>
        
        {endpoint.parameters.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Nessun parametro richiesto
          </Typography>
        ) : (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {endpoint.parameters.map((param) => (
              <Grid item xs={12} sm={6} key={param.name}>
                <TextField
                  fullWidth
                  label={param.name}
                  value={parameterValues[endpoint.id]?.[param.name] || ''}
                  onChange={(e) => handleParameterChange(endpoint.id, param.name, e.target.value)}
                  placeholder={param.default || ''}
                  helperText={`${param.description} ${param.required ? '(Required)' : ''}`}
                  required={param.required}
                  size="small"
                  variant="outlined"
                />
              </Grid>
            ))}
          </Grid>
        )}

        <Box display="flex" gap={2} mb={3}>
          <Button
            variant="contained"
            size="large"
            startIcon={testingEndpoint === endpoint.id ? <CircularProgress size={20} /> : <PlayArrow />}
            onClick={() => testEndpoint(endpoint)}
            disabled={testingEndpoint === endpoint.id}
          >
            {testingEndpoint === endpoint.id ? 'Testing...' : 'Test Endpoint'}
          </Button>
          
          {result && (
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => copyToClipboard(JSON.stringify(result, null, 2))}
            >
              Copy Result
            </Button>
          )}
        </Box>

        {result && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" gutterBottom>
                Risposta
              </Typography>
              <ToggleButtonGroup
                value={responseViewMode}
                exclusive
                onChange={(e, newMode) => newMode && setResponseViewMode(newMode)}
                size="small"
              >
                <ToggleButton value="json">
                  <Code sx={{ mr: 1 }} fontSize="small" />
                  JSON
                </ToggleButton>
                <ToggleButton value="tree">
                  <AccountTree sx={{ mr: 1 }} fontSize="small" />
                  Tree
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            <Box mb={2}>
              <Chip
                label={result.success ? 
                  `✅ ${result.httpStatus || 200} OK` : 
                  `❌ ${result.httpStatus || 'Error'}`
                }
                color={result.success ? 'success' : 'error'}
              />
              {result.duration && (
                <Chip 
                  label={`⏱️ ${result.duration}ms`} 
                  variant="outlined" 
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
            
            <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
              {responseViewMode === 'json' ? (
                <pre
                  style={{
                    margin: 0,
                    padding: '16px',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    backgroundColor: '#1e1e1e',
                    color: '#d4d4d4',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {JSON.stringify(result, null, 2)}
                </pre>
              ) : (
                <Box sx={{ p: 1 }}>
                  <List dense>
                    <JsonTreeItem data={result} nodeKey="root" level={0} />
                  </List>
                </Box>
              )}
            </Paper>
            
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Testato: {new Date(result.timestamp).toLocaleString()}
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          <BugReport sx={{ mr: 1, verticalAlign: 'middle' }} />
          Test Endpoints ReCUP
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Testa tutti gli endpoint ReCUP con interfaccia avanzata TreeView • {flatEndpoints.length} endpoint disponibili • {Object.keys(structuredData).length} categorie
        </Typography>
      </Box>

      {/* Authentication Status */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
            Stato Autenticazione ReCUP
          </Typography>
          {authStatus ? (
            <Box>
              <Chip
                label={authStatus.success && authStatus.data?.authenticated ? 'Autenticato' : 'Non Autenticato'}
                color={authStatus.success && authStatus.data?.authenticated ? 'success' : 'error'}
                icon={authStatus.success && authStatus.data?.authenticated ? <CheckCircle /> : <Error />}
              />
              {authStatus.success && authStatus.data?.authenticated && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Token scade: {new Date(authStatus.data.tokenExpiry).toLocaleString()}
                </Typography>
              )}
              {authStatus.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {authStatus.error}
                </Alert>
              )}
            </Box>
          ) : (
            <Typography>Caricamento stato autenticazione...</Typography>
          )}
        </CardContent>
      </Card>

      {/* Filters and Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Ricerca"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca endpoint..."
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Versione</InputLabel>
                <Select
                  value={filterVersion}
                  label="Versione"
                  onChange={(e) => setFilterVersion(e.target.value)}
                >
                  <MenuItem value="all">Tutte</MenuItem>
                  <MenuItem value="v4">v4</MenuItem>
                  <MenuItem value="v3">v3</MenuItem>
                  <MenuItem value="v2">v2</MenuItem>
                  <MenuItem value="v1">v1</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Priorità</InputLabel>
                <Select
                  value={filterPriority}
                  label="Priorità"
                  onChange={(e) => setFilterPriority(e.target.value)}
                >
                  <MenuItem value="all">Tutte</MenuItem>
                  <MenuItem value="high">Alta</MenuItem>
                  <MenuItem value="medium">Media</MenuItem>
                  <MenuItem value="low">Bassa</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyV4Preferred}
                    onChange={(e) => setOnlyV4Preferred(e.target.checked)}
                  />
                }
                label="Solo v4 Preferiti"
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box display="flex" gap={1}>
                <IconButton 
                  onClick={() => setListViewMode('tree')}
                  color={listViewMode === 'tree' ? 'primary' : 'default'}
                >
                  <ViewModule />
                </IconButton>
                <IconButton 
                  onClick={() => setListViewMode('list')}
                  color={listViewMode === 'list' ? 'primary' : 'default'}
                >
                  <ViewList />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={selectedEndpoint ? 6 : 12}>
          <Typography variant="h5" gutterBottom>
            Endpoint ({getFilteredEndpoints().length})
          </Typography>
          {listViewMode === 'tree' ? renderTreeView() : renderListView()}
        </Grid>
        
        {selectedEndpoint && (
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              Dettagli & Test
            </Typography>
            {renderEndpointDetails()}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default TestEndpointsPage;